import type { QueryClient } from '@tanstack/react-query';
import type { CorSelo } from '../../componentes/base';

export type TipoBeneficio = 'vale_refeicao' | 'vale_transporte' | 'plano_saude' | 'outro';
export type Natureza = 'provento' | 'desconto' | 'encargo';
export type EstadoCompetencia = 'aberta' | 'calculada' | 'conferida' | 'fechando' | 'fechada';

export const TIPOS_BENEFICIO: TipoBeneficio[] = ['vale_refeicao', 'vale_transporte', 'plano_saude', 'outro'];
export const NATUREZAS: Natureza[] = ['provento', 'desconto', 'encargo'];
export const INCIDENCIAS = ['inss', 'irrf', 'fgts'] as const;

export const COR_NATUREZA: Record<Natureza, CorSelo> = { provento: 'verde', desconto: 'laranja', encargo: 'azul' };
export const COR_ESTADO: Record<EstadoCompetencia, CorSelo> = { aberta: 'neutro', calculada: 'azul', conferida: 'roxo', fechando: 'laranja', fechada: 'verde' };
export const COR_TIPO_BENEFICIO: Record<TipoBeneficio, CorSelo> = { vale_refeicao: 'verde', vale_transporte: 'ciano', plano_saude: 'roxo', outro: 'neutro' };

export interface Beneficio {
  id: string;
  nome: string;
  tipo: TipoBeneficio;
  forma: 'valor' | 'percentual';
  valor_centavos: number | null;
  percentual: number | null;
  moeda: string;
  desconto_forma: 'nenhum' | 'valor' | 'percentual';
  desconto_valor_centavos: number | null;
  desconto_percentual: number | null;
  aplica_a: 'funcionario' | 'funcao';
  alvo_ids: string[];
  alvos: { id: string; nome: string }[];
  ativo: boolean;
}

export interface Evento {
  id: string;
  codigo: string;
  nome: string;
  natureza: Natureza;
  tipo: 'fixo' | 'variavel';
  forma: 'valor' | 'percentual';
  valor_centavos: number | null;
  percentual: number | null;
  incide_em: string[];
  especial: string | null;
  ativo: boolean;
}

export interface Competencia {
  id: string;
  ano_mes: string;
  estado: EstadoCompetencia;
  data_pagamento: string | null;
  observacoes: string | null;
  funcionarios: number;
  proventos_centavos: number;
  descontos_centavos: number;
  encargos_centavos: number;
  liquido_centavos: number;
  custo_centavos: number;
  moeda: string;
  calculada_em: string | null;
  conferida_em: string | null;
  conferida_por_nome: string | null;
  fechada_em: string | null;
  fechada_por_nome: string | null;
  fechamento_job_id: string | null;
}

export interface CompetenciaDetalhe extends Competencia {
  /** Review r14 segurança (B3): `centro_nome` nulo para o centro do Financeiro quando quem lê não tem acesso ao Financeiro (`centros_ocultos`). */
  centros: { id: string; centro_custo_id: string | null; centro_nome: string | null; funcionarios: number; custo_centavos: number; lancamento_id: string | null }[];
  centros_ocultos?: boolean;
  job: { estado: 'pendente' | 'rodando' | 'concluido' | 'erro'; progresso: number; erro: string | null; resultado: Record<string, unknown> | null } | null;
  variaveis_total: number;
}

export interface Variavel {
  id: string;
  competencia_id: string;
  funcionario_id: string;
  funcionario_nome: string;
  evento_id: string;
  evento_codigo: string;
  evento_nome: string;
  natureza: Natureza;
  quantidade: number;
  valor_centavos: number | null;
  descricao: string | null;
  /** Pacote 2: `despesas` no reembolso lançado pelo Despesas (não se remove pela Folha). */
  origem_modulo?: string | null;
}

export interface Holerite {
  id: string;
  competencia_id: string;
  ano_mes: string;
  competencia_estado: EstadoCompetencia;
  funcionario_id: string;
  funcionario_nome: string;
  funcao_nome: string | null;
  departamento_nome: string | null;
  contrato_tipo: string;
  salario_base_centavos: number;
  proventos_centavos: number;
  descontos_centavos: number;
  encargos_centavos: number;
  liquido_centavos: number;
  moeda: string;
  anexo_id: string | null;
  email_enviado_em: string | null;
}

export interface HoleriteDetalhe extends Holerite {
  funcionario_cpf: string | null;
  itens: { id: string; codigo: string; nome: string; natureza: Natureza; quantidade: number | null; base_centavos: number | null; aliquota_bp: number | null; valor_centavos: number }[];
}

export function limpar(v: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x === '' ? null : x]));
}

/** AAAA-MM para MM/AAAA, o formato que se lê numa folha de pagamento. */
export function rotuloCompetencia(anoMes: string): string {
  return `${anoMes.slice(5)}/${anoMes.slice(0, 4)}`;
}

/** Depois de qualquer mudança na folha, as listas e os detalhes são lidos de novo. */
export async function invalidarFolha(qc: QueryClient) {
  await Promise.all([
    qc.invalidateQueries({ queryKey: ['/folha/beneficios'] }),
    qc.invalidateQueries({ queryKey: ['/folha/eventos'] }),
    qc.invalidateQueries({ queryKey: ['/folha/competencias'] }),
    qc.invalidateQueries({ queryKey: ['/folha/holerites'] }),
    qc.invalidateQueries({ queryKey: ['folha'] }),
  ]);
}
